/**
 * Folder list maintenance (spec §7.1 `refreshFolders`/`scheduleFolderCount`, §8 folder actions):
 * server refresh with debounce, create / rename / delete, mark-all-read and empty.
 */
import { api } from '$lib/api/client';
import { db, purgeFolder } from '$lib/db';
import type { FolderRec } from '$lib/types';
import { plainFolder } from './list.svelte';
import type { MailStore } from './mail.svelte';

export const FOLDER_COUNT_DEBOUNCE_MS = 2000;
/** A local action within this window postpones the folder refresh (counters would flicker). */
export const LOCAL_ACT_QUIET_MS = 1500;

export class FoldersModule {
  private inFlight = false;
  private lastAt = 0;
  private timer: ReturnType<typeof setTimeout> | undefined;
  constructor(private readonly s: MailStore) {}

  /** GET /api/folders → IndexedDB → state. Single-flight, skipped offline. */
  async refreshFolders(): Promise<void> {
    if (this.inFlight || this.s.offline || !this.s.user) return;
    this.inFlight = true;
    try {
      const { folders } = await api.folders();
      const list: FolderRec[] = folders.map((f) => ({ path: f.path, name: f.name, specialUse: f.specialUse, delimiter: f.delimiter, messages: f.messages, unseen: f.unseen, modseq: f.modseq }));
      await db.folders.bulkPut(list);
      this.s.folders = list;
      this.lastAt = Date.now();
    } finally {
      this.inFlight = false;
    }
  }

  /** Stop the debounce timer (shutdown / test teardown). */
  dispose(): void {
    clearTimeout(this.timer);
    this.timer = undefined;
  }

  /** Debounced refresh ≥ 2 s after the last one; postponed while ops are queued. */
  scheduleFolderCount(ms = FOLDER_COUNT_DEBOUNCE_MS): void {
    clearTimeout(this.timer);
    this.timer = setTimeout(async () => {
      if ((await db.ops.count()) > 0 || Date.now() - this.s.lastLocalAct < LOCAL_ACT_QUIET_MS) {
        this.scheduleFolderCount(FOLDER_COUNT_DEBOUNCE_MS);
        return;
      }
      this.refreshFolders().catch(() => undefined);
    }, Math.max(ms, FOLDER_COUNT_DEBOUNCE_MS - (Date.now() - this.lastAt)));
  }

  async newFolder(name: string, parent?: string): Promise<void> {
    try {
      await api.createFolder(name, parent);
      await this.s.refresh();
      this.s.showToast('folderCreated');
    } catch {
      this.s.showToast('netError');
    }
  }

  /** Rename; cached rows/folders under the old prefix are dropped, the current folder follows. */
  async renameFolder(path: string, name: string, parent?: string): Promise<void> {
    try {
      const r = await api.renameFolder(path, name, parent);
      for (const p of this.s.folders.filter((f) => f.path.startsWith(path)).map((f) => f.path)) {
        await db.messages.where('folder').equals(p).delete();
        await db.folders.delete(p);
      }
      if (this.s.currentFolder === path) {
        await this.s.setFolder(r.path);
        this.s.navigate({ name: 'list', folder: r.path });
      }
      await this.s.refresh();
      this.s.showToast('folderRenamed');
    } catch {
      this.s.showToast('netError');
    }
  }

  async deleteFolder(path: string): Promise<void> {
    try {
      await api.deleteFolder(path);
      await db.messages.where('folder').equals(path).delete();
      await db.folders.delete(path);
      if (this.s.currentFolder === path) await this.s.openFolder('INBOX');
      else await this.s.refresh();
      this.s.showToast('folderDeleted');
    } catch {
      this.s.showToast('netError');
    }
  }

  /** Optimistic: `unseen=0`, every cached row gets `\Seen`, then resync. */
  async markFolderRead(folder = this.s.currentFolder): Promise<void> {
    try {
      await api.markFolderRead(folder);
    } catch {
      this.s.showToast('actionFailed');
      return;
    }
    const f = this.s.folders.find((x) => x.path === folder);
    if (f) {
      f.unseen = 0;
      this.s.folders = [...this.s.folders];
      await db.folders.put(plainFolder(f));
    }
    const rows = (await db.messages.where('folder').equals(folder).toArray()).filter((r) => !r.flags.includes('\\Seen')).map((r) => ({ ...r, flags: [...r.flags, '\\Seen'] }));
    if (rows.length) await db.messages.bulkPut(rows);
    if (this.s.currentFolder === folder) await this.s.loadListFromCache(folder);
    this.s.showToast('doneToast');
    void this.s.syncFolder(folder).catch(() => undefined);
    void this.refreshFolders().catch(() => undefined);
  }

  /** Trash/Junk only: server purge, then local purge and resync. */
  async emptyFolder(): Promise<void> {
    const folder = this.s.currentFolder;
    if (!this.s.canEmpty()) return;
    try {
      await api.emptyFolder(folder);
      await purgeFolder(folder);
      this.s.clearSelection();
      await this.s.loadListFromCache(folder);
      await this.s.list.countLoaded(folder);
      await this.refreshFolders();
      await this.s.syncFolder(folder).catch(() => undefined);
      this.s.showToast('purgedToast');
    } catch {
      this.s.showToast('actionFailed');
    }
  }
}
